import { useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import WorkCard from "@/components/base/WorkCard";
import { filterWorks, genreOptions, regionOptions, yearOptions, sortOptions } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const isMovie = type === "movie";
  const typeLabel = isMovie ? "电影" : "电视剧";
  const valid = type === "movie" || type === "series";

  const [genre, setGenre] = useState("");
  const [region, setRegion] = useState("");
  const [year, setYear] = useState<number | null>(null);
  const [sort, setSort] = useState("heat");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  usePageMeta(
    valid
      ? `${isMovie ? "最新电影" : "热播电视剧"} - 疯狂的欲望影视资讯`
      : "分类 - 疯狂的欲望影视资讯",
    valid
      ? isMovie
        ? "疯狂的欲望最新电影列表，涵盖悬疑、科幻、剧情等类型，支持按类型、地区、年份与热度筛选，快速找到想看的院线佳作。"
        : "疯狂的欲望热播电视剧列表，涵盖悬疑、科幻、都市等类型，支持按类型、地区、年份与热度筛选，追剧前先做足功课。"
      : undefined,
  );

  const results = useMemo(() => {
    if (!valid) return [];
    return filterWorks({ type, genre, region, year, sort });
  }, [type, valid, genre, region, year, sort]);

  const shown = results.slice(0, visible);
  const hasFilter = Boolean(genre || region || year);

  const resetAll = () => {
    setGenre("");
    setRegion("");
    setYear(null);
    setSort("heat");
    setVisible(PAGE_SIZE);
  };

  if (!valid) {
    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-[1400px] flex-col items-center justify-center px-4 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这个分类</h1>
        <p className="mt-2 text-sm text-foreground-600">试试从电影或电视剧列表开始浏览。</p>
        <div className="mt-6 flex gap-3">
          <Link to="/category/movie" className="cursor-pointer rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700">
            浏览电影
          </Link>
          <Link to="/category/series" className="cursor-pointer rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-400">
            浏览电视剧
          </Link>
        </div>
      </div>
    );
  }

  const filterControls = (
    <>
      <FilterGroup label="类型">
        <Chip active={genre === ""} onClick={() => setGenre("")}>全部</Chip>
        {genreOptions.map((g) => (
          <Chip key={g} active={genre === g} onClick={() => setGenre(g)}>{g}</Chip>
        ))}
      </FilterGroup>
      <FilterGroup label="地区">
        <Chip active={region === ""} onClick={() => setRegion("")}>全部</Chip>
        {regionOptions.map((r) => (
          <Chip key={r} active={region === r} onClick={() => setRegion(r)}>{r}</Chip>
        ))}
      </FilterGroup>
      <FilterGroup label="年份">
        <Chip active={year === null} onClick={() => setYear(null)}>全部</Chip>
        {yearOptions.map((y) => (
          <Chip key={y} active={year === y} onClick={() => setYear(y)}>{y}</Chip>
        ))}
      </FilterGroup>
    </>
  );

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
          <nav className="mb-3 flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{typeLabel}</span>
          </nav>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            {isMovie ? "最新电影" : "热播电视剧"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            {isMovie
              ? "汇集院线热映与即将上映的电影，附剧情简介、演员阵容与评分参考。"
              : "精选热播与高分剧集，帮你快速判断哪一部值得投入时间追下去。"}
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground-800">
            <i className="ri-filter-3-line text-primary-600" />
            排序
          </span>
          <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { setSort(opt.value); setVisible(PAGE_SIZE); }}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  sort === opt.value ? "bg-primary-600 text-background-50" : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="ml-auto flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 lg:hidden"
          >
            <i className="ri-equalizer-line" />
            筛选{hasFilter ? " · 已选" : ""}
          </button>
        </div>

        <div className="flex gap-8">
          <aside className="hidden w-56 shrink-0 lg:block">
            <div className="sticky top-24 space-y-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
                <button type="button" onClick={resetAll} className="cursor-pointer text-xs text-foreground-500 hover:text-primary-700">
                  重置
                </button>
              </div>
              {filterControls}
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <p className="mb-4 text-sm text-foreground-500">
              共 <span className="font-semibold text-foreground-950">{results.length}</span> 部{typeLabel}
            </p>

            {shown.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-6">
                  {shown.map((work) => (
                    <WorkCard key={work.id} work={work} showHeat />
                  ))}
                </div>
                {visible < results.length && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                    >
                      加载更多（剩余 {results.length - visible} 部）
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
                <i className="ri-inbox-line text-5xl text-foreground-400" />
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有符合条件的结果</h3>
                <p className="mt-2 text-sm text-foreground-600">换个筛选条件试试，或者重置全部条件。</p>
                <button type="button" onClick={resetAll} className="mt-5 cursor-pointer rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700">
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] flex lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setDrawerOpen(false)} />
          <div className="relative ml-auto flex h-full w-full max-w-xs flex-col bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-4">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
              <button type="button" aria-label="关闭" onClick={() => setDrawerOpen(false)} className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 hover:bg-background-100">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="flex-1 space-y-6 overflow-y-auto p-5">{filterControls}</div>
            <div className="flex gap-3 border-t border-background-200 p-4">
              <button type="button" onClick={resetAll} className="flex-1 cursor-pointer rounded-md border border-background-300 py-2.5 text-sm font-medium text-foreground-700">
                重置
              </button>
              <button type="button" onClick={() => setDrawerOpen(false)} className="flex-1 cursor-pointer rounded-md bg-primary-600 py-2.5 text-sm font-medium text-background-50">
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-foreground-500">{label}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
        active
          ? "bg-secondary-500 text-background-50"
          : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900"
      }`}
    >
      {children}
    </button>
  );
}